import { Outlet, NavLink, Link } from 'react-router-dom';
import { Ticket, ShoppingCart, Menu, X, User } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '@services/publicApi';
import { usePublicStore } from '@store/publicStore';
import { useAuthStore } from '@store/authStore';
import { cn } from '@utils';

export function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setStorefrontData, tenantBranding, brandInfo, getCartItemCount } = usePublicStore();
  const { isAuthenticated } = useAuthStore();
  const cartCount = usePublicStore(s => s.getCartItemCount());

  const { data } = useQuery({
    queryKey: ['storefront'],
    queryFn: () => publicApi.getStorefront(),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data?.data) {
      setStorefrontData(data.data.tenant, data.data.brand);
    }
  }, [data]);

  const brandName = brandInfo?.name || tenantBranding?.name || 'Events';
  const logo = brandInfo?.logo || tenantBranding?.logo;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 dark:bg-gray-950/80 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt={brandName} className="h-9 w-auto object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-lg font-bold text-gray-900 dark:text-white hidden sm:block">
                {brandName}
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800'
                  )
                }
              >
                Home
              </NavLink>
              <NavLink
                to="/events"
                className={({ isActive }) =>
                  cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800'
                  )
                }
              >
                Events
              </NavLink>
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Cart */}
              <Link
                to="/checkout"
                className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ShoppingCart className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Admin / Login */}
              <Link
                to={isAuthenticated ? '/admin/dashboard' : '/login'}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
              >
                <User className="w-4 h-4" />
                {isAuthenticated ? 'Admin' : 'Login'}
              </Link>

              {/* Mobile menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-3 border-t border-gray-100 dark:border-gray-800 space-y-1">
              <NavLink
                to="/"
                end
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Home
              </NavLink>
              <NavLink
                to="/events"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Events
              </NavLink>
              <Link
                to={isAuthenticated ? '/admin/dashboard' : '/login'}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {isAuthenticated ? 'Admin Panel' : 'Login'}
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                {logo ? (
                  <img src={logo} alt={brandName} className="h-8 w-auto object-contain" />
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                    <Ticket className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="text-lg font-bold text-gray-900 dark:text-white">{brandName}</span>
              </div>
              {brandInfo?.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">{brandInfo.description}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-8 text-sm">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 dark:text-white">Quick Links</h4>
                <Link to="/" className="block text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Home</Link>
                <Link to="/events" className="block text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Events</Link>
              </div>
              {(brandInfo?.termsConditions || brandInfo?.privacyPolicy || brandInfo?.refundPolicy) && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Legal</h4>
                  {brandInfo?.termsConditions && <span className="block text-gray-500 cursor-pointer hover:text-gray-700">Terms & Conditions</span>}
                  {brandInfo?.privacyPolicy && <span className="block text-gray-500 cursor-pointer hover:text-gray-700">Privacy Policy</span>}
                  {brandInfo?.refundPolicy && <span className="block text-gray-500 cursor-pointer hover:text-gray-700">Refund Policy</span>}
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800 text-center text-sm text-gray-400">
            &copy; {new Date().getFullYear()} {brandName}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
